import { FunctionComponent, useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import {
  Text,
  Button,
  toast,
  useMediaQuery,
  getTheme,
  PinInput,
  ConexaLogo,
  Loader,
} from '@conexasaude/conexa-hero'

import { Icon } from '@conexasaude/icons'
import ReactCountdown from 'react-countdown'
import AuthLayout from '@/components/AuthLayout'
import { RenderIf } from '@/components/RenderIf'
import { useAuthContext } from '../../contexts/AuthContext'
import { maskEmail } from './utils'
import { useStyles } from './styles'
import { getCache, removeCache, setCache } from '@/utils/cache'
import { DateTime } from 'luxon'
import { useSend2FACode, useValidate2FACode } from '../../queries'
import {
  REQUESTED_2FA_TIME,
  AUTHENTICATED_2FA_TIME,
  MAX_CODE_ATTEMPTS,
  EXPIRED_CODE_ERROR,
} from './constants'
import { openWhatsappSupport } from '@/utils/openWhatsappSupport'
import { TwoFactorAuthParams } from './types'
import { HeroWrapper } from '@/styles/rebranded/HeroWrapper'

function getInitialCountdownDate() {
  const requested2FATime = getCache<number>(REQUESTED_2FA_TIME)

  if (!requested2FATime) {
    const date = DateTime.now().plus({ minutes: 5 }).toJSDate().getTime()

    return date
  }

  const requestedDate = DateTime.fromMillis(requested2FATime)
    .plus({ minutes: 5 })
    .toJSDate()
    .getTime()

  return requestedDate
}

const TwoFactorAuth: FunctionComponent = () => {
  const [logoLoadingError, setLogoLoadingError] = useState(false)
  const [validationCode, setValidationCode] = useState('')
  const [countdownMs, setCountdownMs] = useState(getInitialCountdownDate)
  const [isCountdownExpired, setIsCountdownExpired] = useState(
    getInitialCountdownDate() <= 0,
  )

  const params = useParams<TwoFactorAuthParams>()
  const history = useHistory()
  const { user, users, signOut } = useAuthContext()
  const theme = getTheme()
  const isMobile = useMediaQuery(`(max-width: ${theme.grid.breakpoint.sm})`)
  const queryClient = useQueryClient()
  const styles = useStyles()

  const hasAuthenticated = getCache<number>(AUTHENTICATED_2FA_TIME)

  const {
    refetch: refetchValidate2FACode,
    fetchStatus: validate2FACodeFetchStatus,
    error: validate2FACodeError,
    isSuccess: validate2FACodeSuccess,
    errorUpdateCount: validate2FACodeErrorCount,
  } = useValidate2FACode(validationCode)
  const {
    data: send2FACodeData,
    refetch: refetchSend2FACode,
    fetchStatus: send2FACodeFetchStatus,
    isLoading: send2FACodeLoading,
    error: send2FACodeError,
  } = useSend2FACode(!hasAuthenticated && !validate2FACodeSuccess)

  const showValidationCodeBlock =
    !isCountdownExpired &&
    validate2FACodeFetchStatus !== 'fetching' &&
    !validate2FACodeSuccess &&
    send2FACodeFetchStatus !== 'fetching' &&
    !send2FACodeError

  const showValidationCodeText =
    !isCountdownExpired &&
    validate2FACodeFetchStatus !== 'fetching' &&
    !validate2FACodeError

  const showExpiredCodeBlock =
    isCountdownExpired && !validate2FACodeSuccess && !send2FACodeError

  function handleGoBack() {
    signOut(users?.length === 1)
    queryClient.clear()
  }

  async function handleSendNew2FACode() {
    const { data } = await refetchSend2FACode()

    const newCodeSentAtDate = DateTime.fromMillis(data?.codeSentAt)
      .plus({ minutes: 5 })
      .toJSDate()
      .getTime()

    setCountdownMs(newCodeSentAtDate)
    setValidationCode('')
    setIsCountdownExpired(false)
    setCache(REQUESTED_2FA_TIME, newCodeSentAtDate)
  }

  useEffect(() => {
    if (!send2FACodeData?.codeSentAt) return

    const requested2FATime = send2FACodeData?.codeSentAt

    setCache(REQUESTED_2FA_TIME, requested2FATime)
    setCountdownMs(
      DateTime.fromJSDate(new Date(requested2FATime))
        .plus({ minutes: 5 })
        .toJSDate()
        .getTime(),
    )
  }, [send2FACodeData?.codeSentAt])

  useEffect(() => {
    if (!send2FACodeError) return

    removeCache(REQUESTED_2FA_TIME)
    setCountdownMs(new Date().getTime())
  }, [send2FACodeError])

  useEffect(() => {
    if (!validate2FACodeError) return

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const error = validate2FACodeError as Record<string, any>

    if (error?.response?.data?.object === EXPIRED_CODE_ERROR) {
      toast({
        message: 'Código expirado!',
        variant: 'error',
      })
      signOut(true)
      queryClient.clear()
    }
  }, [validate2FACodeError, signOut, queryClient])

  useEffect(() => {
    if (validate2FACodeSuccess) {
      setCache(AUTHENTICATED_2FA_TIME, new Date().getTime())

      setTimeout(() => {
        history.push('/')
      }, 2000)
    }

    return () => {
      queryClient.clear()
    }
  }, [history, queryClient, validate2FACodeSuccess])

  useEffect(() => {
    if (!validate2FACodeErrorCount) return

    if (
      validate2FACodeErrorCount + send2FACodeData?.attemptsCount ===
      MAX_CODE_ATTEMPTS
    ) {
      toast({
        message: 'Tentativas de validação excedidas.',
        variant: 'error',
      })

      signOut(true)
      queryClient.clear()
    }
  }, [
    validate2FACodeErrorCount,
    send2FACodeData?.attemptsCount,
    signOut,
    queryClient,
  ])

  return (
    <AuthLayout
      customFooter={
        <Text
          variant="linkUnderline"
          css={styles.supportLink}
          onClick={openWhatsappSupport}
        >
          Conversar com suporte
        </Text>
      }
    >
      <HeroWrapper>
        <RenderIf condition={isMobile}>
          <div css={styles.headerWrapper}>
            <div css={styles.headerContent}>
              <RenderIf condition={!!params.clinic && !logoLoadingError}>
                <div css={styles.logoWhitelabel}>
                  <img
                    src={`${process.env.REACT_APP_LINK_LOGO_AWS}${params.clinic}.png`}
                    alt=""
                    onError={event => {
                      event.currentTarget.onerror = null
                      setLogoLoadingError(true)
                      event.currentTarget.style.height = 'auto'
                    }}
                  />
                </div>
              </RenderIf>
              <RenderIf condition={!params.clinic || logoLoadingError}>
                <div css={styles.logoConexa}>
                  <ConexaLogo base="colorful" inverse width={125} />
                </div>
              </RenderIf>
            </div>
          </div>
        </RenderIf>

        <div css={styles.contentWrapper}>
          <Text variant="link" css={styles.backLink} onClick={handleGoBack}>
            <Icon name="arrow-left" />
            Voltar
          </Text>

          <RenderIf condition={send2FACodeLoading}>
            <div css={styles.loaderWrapper}>
              <Loader size="large" />
            </div>
          </RenderIf>

          <RenderIf condition={!send2FACodeLoading}>
            <Text variant="headingMedium">Informe o código de validação</Text>

            <Text css={styles.validationText}>
              Digite o código de validação de identidade que enviamos por e-mail
              para {maskEmail(user?.email)}
            </Text>
            <div css={styles.codeInputWrapper}>
              <PinInput
                name="validationCode"
                inputType="text"
                length={6}
                value={validationCode}
                onChange={value => setValidationCode(value)}
                disabled={
                  (isCountdownExpired && !validate2FACodeSuccess) ||
                  !!send2FACodeError
                }
                validated={validate2FACodeSuccess}
                inputMode="text"
              />
            </div>

            <RenderIf condition={showValidationCodeBlock}>
              <RenderIf condition={showValidationCodeText}>
                <Text css={styles.validationTimeText}>
                  Você tem até 5 minutos para validar o código
                </Text>

                <ReactCountdown
                  date={countdownMs}
                  onComplete={() => {
                    setIsCountdownExpired(true)
                  }}
                  renderer={({ formatted }) => {
                    return (
                      <Text css={styles.validationCodeText}>
                        Não recebeu o código? <br /> Solicite o recebimento de
                        um novo código em{' '}
                        <Text renderAs="span">
                          {formatted.minutes}:{formatted.seconds}
                        </Text>
                      </Text>
                    )
                  }}
                />
              </RenderIf>

              <RenderIf condition={!showValidationCodeText}>
                <Text css={styles.errorText}>
                  <Icon name="close-circle" size="small" /> Código incorreto.
                  Tente novamente.
                </Text>
              </RenderIf>

              <Button
                fullWidth
                disabled={validationCode?.length !== 6}
                onClick={() => refetchValidate2FACode()}
              >
                Validar código
              </Button>
            </RenderIf>

            <RenderIf condition={!!send2FACodeError}>
              <Text css={styles.errorText}>
                <Icon name="close-circle" size="small" /> Falha ao enviar codigo
                de autenticação
              </Text>
              <Button
                fullWidth
                busy={send2FACodeFetchStatus === 'fetching'}
                onClick={handleSendNew2FACode}
              >
                Receber novo código
              </Button>
            </RenderIf>

            <RenderIf condition={showExpiredCodeBlock}>
              <Text css={styles.errorText}>
                <Icon name="close-circle" size="small" /> Seu código expirou
              </Text>
              <Button
                fullWidth
                busy={send2FACodeFetchStatus === 'fetching'}
                onClick={handleSendNew2FACode}
              >
                Receber novo código
              </Button>
            </RenderIf>

            <RenderIf condition={validate2FACodeSuccess}>
              <Text css={styles.successText}>
                <Icon name="success" size="small" /> Código validado.
              </Text>
            </RenderIf>

            <RenderIf
              condition={
                validate2FACodeFetchStatus === 'fetching' ||
                send2FACodeFetchStatus === 'fetching'
              }
            >
              <div css={styles.loaderWrapper}>
                <Loader size="large" />
              </div>
            </RenderIf>
          </RenderIf>
        </div>
      </HeroWrapper>
    </AuthLayout>
  )
}

export default TwoFactorAuth
